import { useEffect, useState } from 'react';
import { Archive, Check, MapPin, RotateCw, ShoppingBag, X } from 'lucide-react';
import type { Character } from '../shared/types';
import { furniture, getFurniture, homes, text, type Locale } from '../shared/content';
import { homeComfort, placementIssue } from '../shared/room-rules';
import { translate } from './i18n';
import RoomScene from './scene/RoomScene';
const copy = {
  slots: text('Pieces on display', '已摆放家具', '已擺放家具', 'Muebles colocados'),
  rotate: text('Rotate 90°', '旋转 90°', '旋轉 90°', 'Girar 90°'),
  store: text('Put away · free', '收起 · 免费', '收起 · 免費', 'Guardar · gratis'),
  move: text('Move / rotate', '移动／旋转', '移動／旋轉', 'Mover / girar'),
  position: text('Place at this position', '摆在这个位置', '擺在這個位置', 'Colocar aquí'),
  x: text('Position · left / right', '位置 · 左右', '位置 · 左右', 'Posición · izquierda / derecha'),
  z: text('Position · front / back', '位置 · 前后', '位置 · 前後', 'Posición · delante / detrás'),
  free: text(
    'Move, rotate or put away whenever you like. Stored pieces stay yours.',
    '可随时移动、旋转或免费收起，收起后仍归你所有。',
    '可隨時移動、旋轉或免費收起，收起後仍歸你所有。',
    'Puedes mover, girar o guardar gratis. Los muebles siguen siendo tuyos.',
  ),
  invalid: text(
    'This spot is blocked or the room is full. Choose a green spot or put another piece away.',
    '这个位置被挡住或房间已满。请选择绿色位置，或先收起一件家具。',
    '這個位置被擋住或房間已滿。請選擇綠色位置，或先收起一件家具。',
    'Lugar ocupado o habitación llena. Elige un lugar verde o guarda otro mueble.',
  ),
  comfort: text('Home comfort', '家的舒适度', '家的舒適度', 'Comodidad del hogar'),
  rest: text('Rest bonus', '休息额外恢复', '休息額外恢復', 'Extra al descansar'),
  cook: text('Home meal bonus', '家常餐额外饱腹', '家常餐額外飽腹', 'Extra al cocinar'),
  hint: text(
    'Place a seat to rest better, or a breakfast table / coffee corner for more filling home meals. Upgrades add both comfort and display space.',
    '摆上椅子或沙发，休息更舒服；早餐桌或咖啡角让家常餐更满足。升级增加舒适度与摆放数量。',
    '擺上椅子或沙發，休息更舒服；早餐桌或咖啡角讓家常餐更滿足。升級增加舒適度與擺放數量。',
    'Un asiento mejora el descanso; una mesa o rincón del café mejora las comidas. Las mejoras añaden comodidad y espacio para muebles.',
  ),
  permanent: text(
    'One-time purchase · no recurring rent in V1',
    '一次性升级 · V1 无周期租金',
    '一次性升級 · V1 無週期租金',
    'Compra única · V1 sin alquiler periódico',
  ),
};
export default function HomePanel({
  character,
  locale,
  busy,
  onAction,
  onGoHome,
}: {
  character: Character;
  locale: Locale;
  busy: boolean;
  onAction: (type: string, payload: Record<string, unknown>, message?: string) => Promise<unknown>;
  onGoHome: () => void;
}) {
  const p = character.data,
    home = homes.find((h) => h.id === p.home)!,
    next = homes[homes.findIndex((h) => h.id === p.home) + 1];
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key),
    c = (key: keyof typeof copy) => copy[key][locale];
  const [itemId, setItemId] = useState<string | null>(null),
    [rotation, setRotation] = useState(0),
    [x, setX] = useState(0),
    [z, setZ] = useState(1);
  const available = !busy && !p.travel && p.location === 'home';
  const comfort = homeComfort(p),
    item = itemId ? getFurniture(itemId) : undefined;
  const issue = itemId ? placementIssue(p, itemId, x, z, rotation) : null;
  useEffect(() => {
    if (itemId) document.querySelector('.drawer')?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [itemId]);
  const choose = (id: string) => {
    const existing = p.decor.find((d) => d.itemId === id),
      r = existing?.rotation || 0;
    const spots = [
      { x: 0, z: 1 },
      ...Array.from({ length: 49 }, (_, i) => ({ x: (i % 7) - 3, z: Math.floor(i / 7) - 3 })),
    ];
    const spot = existing || spots.find((s) => !placementIssue(p, id, s.x, s.z, r)) || spots[0]!;
    setItemId(id);
    setRotation(r);
    setX(spot.x);
    setZ(spot.z);
  };
  const place = async (px: number, pz: number) => {
    if (!available || !itemId) return;
    const r = await onAction('furniture.place', { itemId, x: px, z: pz, rotation }, t('saved'));
    if (r) setItemId(null);
  };
  return (
    <>
      <div className="section-eyebrow">{home.name[locale]}</div>
      <h2>{t('room')}</h2>
      {(p.location !== 'home' || p.travel) && (
        <button className="inline-tip text-button" disabled={!!p.travel || busy} onClick={onGoHome}>
          <MapPin size={15} />
          {p.travel ? t('traveling') : t('go')}
        </button>
      )}
      <RoomScene
        decor={p.decor}
        locale={locale}
        home={p.home}
        layout={p}
        placing={itemId ? { itemId, rotation, x, z } : undefined}
        onSpot={itemId && available ? place : undefined}
      />
      {item && (
        <section className="placement-controls">
          <div className="placement-heading">
            <strong>{item.name[locale]}</strong>
            <button className="text-button" onClick={() => setItemId(null)} aria-label={t('close')}>
              <X size={17} />
            </button>
          </div>
          <p className="muted small">{t('selectSpot')}</p>
          <button
            className="button soft compact"
            onClick={() => setRotation((r) => (r + 1) % 4)}
            disabled={!available}
          >
            <RotateCw size={14} />
            {c('rotate')} · {rotation * 90}°
          </button>
          <div className="position-fields">
            <label>
              {c('x')}
              <select value={x} onChange={(e) => setX(Number(e.target.value))}>
                {[-3, -2, -1, 0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {c('z')}
              <select value={z} onChange={(e) => setZ(Number(e.target.value))}>
                {[-3, -2, -1, 0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {issue && <p className="placement-warning small">{c('invalid')}</p>}
          <button
            className="button primary full"
            disabled={!available || !!issue}
            onClick={() => place(x, z)}
          >
            {c('position')}
          </button>
        </section>
      )}
      <p className="muted small">
        {c('slots')} · {p.decor.length}/{home.slots}
      </p>
      <p className="muted small">{c('free')}</p>
      <div className="comfort-card">
        <strong>{c('comfort')}</strong>
        <div>
          {c('rest')} +{comfort.rest} · {c('cook')} +{comfort.cook}
        </div>
        <small>{c('hint')}</small>
      </div>
      <p className="muted small">{t('voucherNote')}</p>
      <h3>{t('furniture')}</h3>
      <div className="furniture-grid">
        {furniture.map((f) => {
          const owned = p.furnitureOwned.includes(f.id),
            placed = p.decor.some((d) => d.itemId === f.id);
          return (
            <div className="furniture-card" key={f.id}>
              <div
                className={`furniture-art ${f.kind}`}
                style={{ '--item-color': f.color } as React.CSSProperties}
              >
                <span />
                <i />
              </div>
              <strong>{f.name[locale]}</strong>
              <small>{owned ? t('owned') : `C$${f.price}`}</small>
              <button
                className={`button ${owned ? 'soft' : 'light'} compact`}
                disabled={!available}
                onClick={() =>
                  owned ? choose(f.id) : onAction('furniture.buy', { itemId: f.id }, t('saved'))
                }
              >
                {placed ? <Check size={13} /> : !owned ? <ShoppingBag size={13} /> : null}
                {owned ? (placed ? c('move') : t('place')) : t('buy')}
              </button>
              {placed && (
                <button
                  className="text-button furniture-store"
                  disabled={!available}
                  onClick={() => {
                    if (itemId === f.id) setItemId(null);
                    onAction('furniture.store', { itemId: f.id }, t('saved'));
                  }}
                >
                  <Archive size={12} />
                  {c('store')}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {next && (
        <>
          <button
            className="button soft full"
            disabled={!available}
            onClick={() => onAction('home.upgrade', { homeId: next.id }, t('saved'))}
          >
            {t('upgrade')} · {next.name[locale]} · C${next.cost}
          </button>
          <p className="muted tiny">{c('permanent')}</p>
        </>
      )}
    </>
  );
}
